



import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../../../components/CustomButton";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import toast from "react-hot-toast";
import GeneratedDocumentModal from "./GeneratedDocument";
import { loadTemplate, generatePDF } from "../../../../utils/pdfGenerator";
import { useTranslation } from "react-i18next";
import { useWorkerCompanyProfile } from "../../../../hooks/useWorkerCompanyProfile";

const PreviewModal = ({ open, onClose, data }) => {
  const [generating, setGenerating] = useState(false);
  const [generatedData, setGeneratedData] = useState(null);
  const [openGeneratedModal, setOpenGeneratedModal] = useState(false);
  const [period, setPeriod] = useState("");
  const {t} = useTranslation();
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  
  // Fetch company profile data
  const { companyData, loading: loadingCompany } = useWorkerCompanyProfile(decryptedToken);

  // Determine docType early (safe for first render)
  const docType = data?.document_type;

  // Log data changes to debug period issues - MUST be before early return
  useEffect(() => {
    if (data && data.document_type === "payslip") {
      console.log("PreviewModal - Current data period:", data.preview_data?.period);
      console.log("PreviewModal - Full preview data:", data.preview_data);
    }
  }, [data]);
  
  // Reset state when modal closes - MUST be before early return
  useEffect(() => {
    if (!open) {
      setGenerating(false);
      setGeneratedData(null);
      setOpenGeneratedModal(false);
      setPeriod("");
    }
  }, [open]);

  if (!data) return null; // Safe to return AFTER all hooks

  const preview = data.preview_data || {};

  const formatDateTime = (iso) => {
    if (!iso) return "-";
    try {
    const d = new Date(iso);
      if (isNaN(d.getTime())) {
        return iso; // Return original if invalid date
      }
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    } catch (error) {
      return iso; // Return original on error
    }
  };

  const InfoRow = ({ label, value }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.8,
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#44546F",
        fontFamily: 'Poppins, sans-serif',
       }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          color: "#172B4D",
          fontWeight: 400,
          textAlign: "right",
          maxWidth: 220,
          wordBreak: "break-word",
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        {value || "--"}
      </Typography>
    </Box>
  );




const handleGenerateDocument = async () => {
  setGenerating(true);
  try {
    // Wait for company data to load if still loading
    if (loadingCompany) {
      toast.error("Please wait while company information loads...");
      setGenerating(false);
      return;
    }

    // Format date of joining
    const formatDate = (dateString) => {
      if (!dateString) return "";
      try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) {
          return dateString; // Return original if invalid
        }
        return d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch {
        return dateString;
      }
    };

    // Merge preview data with company data - ensure all template placeholders are filled
    const templateData = {
      // Company data from hook (includes logo, name, address, etc.)
      companyName: companyData?.companyName || data.preview_data?.companyName || "Company Name",
      companyAddress: companyData?.companyAddress || data.preview_data?.companyAddress || "Company Address",
      companyEmail: companyData?.companyEmail || data.preview_data?.companyEmail || "",
      companyPhone: companyData?.companyPhone || data.preview_data?.companyPhone || "",
      companyLogo: companyData?.companyLogo || data.preview_data?.companyLogo || "", // Base64 logo
      authorizedSignatory: companyData?.authorizedSignatory || "Authorized Signatory",
      companyContact: companyData?.companyContact || companyData?.companyEmail || companyData?.companyPhone || data.preview_data?.companyEmail || data.preview_data?.companyPhone || "Contact Information",
      
      // Worker data from preview
      workerName: data.preview_data?.workerName || preview.workerName || "",
      workerId: data.preview_data?.workerId || preview.workerId || "",
      position: data.preview_data?.position || preview.position || "",
      department: data.preview_data?.department || preview.department || "",
      
      // Format dates properly for template
      dateOfJoining: formatDate(data.preview_data?.dateOfJoining || preview.dateOfJoining) || "",
      employmentEndDate: formatDate(data.preview_data?.employmentEndDate || preview.employmentEndDate) || "N/A",
      employmentStatus: data.preview_data?.employmentStatus || preview.employmentStatus || "Active",
      
      // Issue date (current date) - always format properly
      issueDate: (() => {
        try {
          return new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
          });
        } catch {
          return new Date().toISOString().split('T')[0];
        }
      })(),
    };

    // Add payslip-specific fields if document type is payslip
    if (data.document_type === "payslip") {
      const currency = data.preview_data?.currency || preview.currency || "";
      
      // Log the period being used for PDF generation
      const pdfPeriod = data.preview_data?.period || preview.period || "";
      console.log("🔍 Generating PDF for period:", pdfPeriod);
      console.log("🔍 Full preview_data:", data.preview_data);
      
      // Generate allowances rows HTML
      let allowancesRows = "";
      if (data.preview_data?.allowances && data.preview_data.allowances.length > 0) {
        allowancesRows = data.preview_data.allowances.map(a => 
          `<tr><td>${a.name || a.description || 'Allowance'}</td><td class="text-right">${a.amount || '0.00'} ${currency}</td></tr>`
        ).join('');
      } else {
        allowancesRows = '<tr><td>No Allowances</td><td class="text-right">0.00 ' + currency + '</td></tr>';
      }
      
      // Generate deductions rows HTML
      let deductionsRows = "";
      if (data.preview_data?.deductions && data.preview_data.deductions.length > 0) {
        deductionsRows = data.preview_data.deductions.map(d => 
          `<tr><td>${d.name || d.description || 'Deduction'}</td><td class="text-right">${d.amount || '0.00'} ${currency}</td></tr>`
        ).join('');
      } else {
        deductionsRows = '<tr><td>No Deductions</td><td class="text-right">0.00 ' + currency + '</td></tr>';
      }
      
      templateData.period = pdfPeriod;
      templateData.payDate = formatDate(data.preview_data?.paymentDate || preview.paymentDate) || "";
      templateData.paymentDate = formatDate(data.preview_data?.paymentDate || preview.paymentDate) || "";
      templateData.basicSalary = `${data.preview_data?.basicSalary || preview.basicSalary || "0.00"} ${currency}`;
      templateData.totalEarnings = `${data.preview_data?.totalEarnings || preview.totalEarnings || "0.00"} ${currency}`;
      templateData.totalDeductions = `${data.preview_data?.totalDeductions || preview.totalDeductions || "0.00"} ${currency}`;
      templateData.netPay = `${data.preview_data?.netPay || preview.netPay || "0.00"} ${currency}`;
      templateData.grossPay = templateData.totalEarnings; // Gross pay is same as total earnings
      templateData.currency = currency;
      
      // Allowances and deductions rows for template
      templateData.allowancesRows = allowancesRows;
      templateData.deductionsRows = deductionsRows;
      
      // Payment method and bank account (if available)
      templateData.paymentMethod = data.preview_data?.paymentMethod || "N/A";
      templateData.bankAccount = data.preview_data?.bankAccount || "N/A";
      
      // Generated date
      templateData.generatedDate = formatDate(data.preview_data?.systemGenerationDate || preview.systemGenerationDate) || new Date().toLocaleDateString("en-GB");
    }
    
    // Log template data to verify all fields are present
    console.log("📋 Template data prepared:", {
      hasCompanyName: !!templateData.companyName,
      hasWorkerName: !!templateData.workerName,
      hasWorkerId: !!templateData.workerId,
      hasPosition: !!templateData.position,
      hasDepartment: !!templateData.department,
      hasDateOfJoining: !!templateData.dateOfJoining,
      hasIssueDate: !!templateData.issueDate,
      hasLogo: !!templateData.companyLogo,
      logoIsBase64: templateData.companyLogo?.startsWith("data:"),
    });

    console.log("📄 Template data:", templateData);

    // 1️⃣ Generate PDF from template
    const filledHtml = await loadTemplate(
      data.document_type,
      templateData
    );

    console.log("✅ HTML generated, length:", filledHtml.length);

    // Validate HTML has content
    if (!filledHtml || filledHtml.trim().length === 0) {
      throw new Error("Generated HTML is empty. Please check template data.");
    }
    
    // Check if HTML has actual text content (not just empty tags)
    const textOnly = filledHtml.replace(/<[^>]+>/g, "").trim();
    if (textOnly.length < 50) {
      console.warn("⚠️ Filled HTML has very little text content:", textOnly.length, "chars");
      console.warn("⚠️ This might result in an empty PDF");
      console.warn("📋 Text content preview:", textOnly.substring(0, 200));
    }
    
    // Check for unreplaced placeholders
    const unreplaced = filledHtml.match(/{{[^}]+}}/g);
    if (unreplaced && unreplaced.length > 0) {
      console.warn("⚠️ Unreplaced placeholders in HTML:", unreplaced);
      console.warn("⚠️ This will cause empty content in PDF");
    }

    // Generate filename - include period for payslips
    let fileName = `${data.document_type}_${preview.workerName || 'document'}`;
    if (data.document_type === "payslip") {
      // Use period from preview_data to ensure correct period is used
      const periodForFilename = data.preview_data?.period || preview.period || "";
      console.log("📝 Generating filename with period:", periodForFilename);
      if (periodForFilename) {
        // Clean period for filename (remove spaces, slashes, etc.)
        const cleanPeriod = periodForFilename.replace(/[^a-zA-Z0-9-]/g, '_');
        fileName += `_${cleanPeriod}`;
      }
    }
    fileName = `${fileName}.pdf`.replace(/_{2,}/g, '_').replace(/^_+|_+$/g, '');
    console.log("📝 Final filename:", fileName);
    
    console.log("🔄 Starting PDF generation...");
    const blob = await generatePDF(filledHtml, fileName, true);

    // Validate blob was created and is not empty
    if (!blob) {
      throw new Error("PDF blob is null. PDF generation failed.");
    }
    
    if (blob.size === 0) {
      throw new Error("PDF blob is empty. The generated PDF has no content.");
    }

    console.log("✅ PDF blob created successfully, size:", blob.size, "bytes");

    // 2️⃣ Create download URL and show success modal
    const pdfUrl = URL.createObjectURL(blob);
    
    if (!pdfUrl) {
      throw new Error("Failed to create PDF URL from blob.");
    }

    console.log("✅ PDF URL created:", pdfUrl);
    setGeneratedData({ filename: fileName, pdfUrl, blob: blob });

    toast.success(t("automatedDocuments.preview.generateSuccess") || "Document generated successfully!");
    setOpenGeneratedModal(true);
  } catch (error) {
    console.error("❌ Error generating document:", error);
    toast.error(error.message || t("automatedDocuments.preview.generateError") || "Failed to generate document.");
  } finally {
    setGenerating(false);
  }
};



  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
         sx={{
      position: "absolute",
            top: "50%",
      left: "50%",
            transform: "translate(-50%, -50%)",
      width: 460,
            maxHeight: "90vh",
      bgcolor: "#fff",
      borderRadius: 3,
      boxShadow: 24,
            overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      fontFamily: "Poppins, sans-serif",
    }}
        >
              <Box
      sx={{
        overflowY: "auto",
        p: 3,
      }}
    >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                color: "#003149",
                fontFamily: 'Poppins, sans-serif',
              }}
            >
            {t("previewModal.title")}
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Info Section */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <InfoRow label={t("previewModal.labels.name")} value={preview.workerName} />
            <InfoRow label={t("previewModal.labels.id")} value={preview.workerId} />
            <InfoRow label={t("previewModal.labels.department")} value={preview.department} />
            <InfoRow label={t("previewModal.labels.position")}  value={preview.position} />
            <InfoRow
              label={t("previewModal.labels.date_of_joining")}
              value={formatDateTime(preview.dateOfJoining)}
            />
{docType === "employment_certificate" && (
  <InfoRow label={t("previewModal.labels.work_schedule")} value={preview.workSchedule} />
)}            <InfoRow
              label={t("previewModal.labels.salary")}
              value={`${preview.salary || "0.00"} ${preview.currency || ""}`}
            />
            <InfoRow label={t("previewModal.labels.company")} value={preview.companyName} />
            {/* <InfoRow label="Current Date" 
                          value={formatDateTime(preview.currentDate)}
 /> */}

 {docType === "payslip" && (
              <>
  <InfoRow label={t("previewModal.labels.period")} value={preview.period} />
                <InfoRow 
                  label={t("previewModal.labels.payment_date") || "Payment Date"} 
                  value={formatDateTime(preview.paymentDate)} 
                />
                <InfoRow 
                  label={t("previewModal.labels.basic_salary") || "Basic Salary"} 
                  value={`${preview.basicSalary || "0.00"} ${preview.currency || ""}`} 
                />
                <InfoRow 
                  label={t("previewModal.labels.total_earnings") || "Total Earnings"} 
                  value={`${preview.totalEarnings || "0.00"} ${preview.currency || ""}`} 
                />
                <InfoRow 
                  label={t("previewModal.labels.total_deductions") || "Total Deductions"} 
                  value={`${preview.totalDeductions || "0.00"} ${preview.currency || ""}`} 
                />
                <InfoRow 
                  label={t("previewModal.labels.net_pay") || "Net Pay"} 
                  value={`${preview.netPay || "0.00"} ${preview.currency || ""}`} 
                />
                {preview.allowances && preview.allowances.length > 0 && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#44546F", mb: 0.5 }}>
                      {t("previewModal.labels.allowances") || "Allowances"}
                    </Typography>
                    {preview.allowances.map((allowance, idx) => (
                      <Box key={idx} sx={{ pl: 2, fontSize: 12, color: "#172B4D" }}>
                        {allowance.name || allowance.description}: {allowance.amount || "0.00"} {preview.currency || ""}
                      </Box>
                    ))}
                  </Box>
                )}
                {preview.deductions && preview.deductions.length > 0 && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#44546F", mb: 0.5 }}>
                      {t("previewModal.labels.deductions") || "Deductions"}
                    </Typography>
                    {preview.deductions.map((deduction, idx) => (
                      <Box key={idx} sx={{ pl: 2, fontSize: 12, color: "#172B4D" }}>
                        {deduction.name || deduction.description}: {deduction.amount || "0.00"} {preview.currency || ""}
                      </Box>
                    ))}
                  </Box>
                )}
              </>
)}   
 {docType === "employment_certificate" && (
              <InfoRow 
                label={t("previewModal.labels.current_date")} 
                          value={formatDateTime(preview.currentDate)}
 />
)}  
            <InfoRow
              label={t("previewModal.labels.generated_on")}
                            value={formatDateTime(preview.systemGenerationDate)}
            />
          </Box>

          {/* Period Field for Payslip */}
          {/* {docType === "payslip" && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#44546F",
                  mb: 0.5,
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
  {t("previewModal.labels.select_payslip_period")}
              </Typography>
              <TextField
                type="month"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                fullWidth
                InputProps={{
                  sx: {
                    fontSize: 14,
                    height: "42px",
                    fontFamily: "Poppins, sans-serif",
                  },
                }}
              />
            </Box>
          )} */}

          <Divider sx={{ my: 2 }} />

          {/* Generate Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 2 }}>
            <CustomButton
  label={
                generating || loadingCompany ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} color="inherit" />
                    {loadingCompany 
                      ? t("previewModal.buttons.loadingCompany") || "Loading company data..."
                      : t("previewModal.buttons.generating")
                    }
                  </Box>
    ) : (
   t("previewModal.buttons.generate_document")
    )
  }
  size="small"
              disabled={generating || loadingCompany}
  onClick={handleGenerateDocument}
/>
          </Box>
          </Box>
        </Box>
      </Modal>

   <GeneratedDocumentModal
  open={openGeneratedModal}
  onClose={() => setOpenGeneratedModal(false)}
  data={generatedData}
/>


    </>
  );
};

export default PreviewModal;







